/**
 * Dane panelu Silvii ze sklepu (endpointy `silvia/*` WooCommerce) przez funkcję `silviaApi`.
 * Klucz WooCommerce zostaje w sekretach funkcji; przeglądarka wysyła tylko Firebase ID token.
 */

import { wywolajFunkcje } from './funkcje';

async function komunikatBledu(res: Response): Promise<string> {
  const tekst = await res.text();
  try {
    const cialo = JSON.parse(tekst) as { error?: unknown; message?: unknown };
    const opis = cialo.error ?? cialo.message;
    if (typeof opis === 'string' && opis.length > 0) return opis;
  } catch {
    return tekst;
  }
  return tekst;
}

async function zgloszBlad(res: Response): Promise<never> {
  const opis = await komunikatBledu(res);
  if (res.status === 401) {
    throw new Error('Sesja panelu wygasła (401). Zaloguj się ponownie.');
  }
  if (res.status === 403) {
    throw new Error('To konto nie ma uprawnień administratora panelu (403).');
  }
  if (res.status >= 500) {
    throw new Error(`Serwer niedostępny (${res.status}): ${opis}`);
  }
  throw new Error(`Błąd API ${res.status}: ${opis}`);
}

function sciezkaSilvii(endpoint: string, params?: Record<string, string>): string {
  const zapytanie = new URLSearchParams(params ?? {}).toString();
  return `silviaApi/${endpoint}${zapytanie ? `?${zapytanie}` : ''}`;
}

async function apiFetch<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
  const res = await wywolajFunkcje(sciezkaSilvii(endpoint, params), {
    method: 'GET',
    cache: 'no-store',
  });
  if (!res.ok) {
    return zgloszBlad(res);
  }
  return res.json();
}

// ── Types ─────────────────────────────────────────────────────────────────

export interface PendingOrders {
  total: number;
  total_cost: number;
  prepaid: number;
  prepaid_cost: number;
  cod: number;
  cod_cost: number;
  low_stock: number;
  complaints: number;
  dhl: number;
  return_stock: number;
}

export interface TodayStats {
  orders_count: number;
  orders_amount: number;
  onhold_count: number;
  onhold_amount: number;
  meta_ads: number;
  google_ads: number;
  supplier_cost: number;
  transport_cost: number;
  prep_cost: number;
  order_costs: number;
  total_costs: number;
  margin: number;
  margin_pct: number;
}

export interface FinanceItem {
  count: number;
  amount: number;
  label?: string;
}

export interface PayoutForecastDay {
  date: string;
  label: string;
  amount: number;
  count: number;
}

export interface FinanceData {
  przelew: FinanceItem;
  pobranie: FinanceItem;
  dzis: FinanceItem;
  jutro: FinanceItem;
  fb_today: number;
  fb_last_year: number;
  returns_stock: { units: number; value: string };
  payout_forecast: PayoutForecastDay[];
}

export interface HealthCheck {
  status: string;
  label: string;
  value: string | number | null;
}

export interface HealthSummary {
  score: number;
  status: string;
  checks: Record<string, HealthCheck>;
}

export interface RumMetric {
  avg: number;
  min: number;
  max: number;
  cnt: number;
}

export interface RumSummary {
  averages: Record<string, RumMetric>;
  visitors: number;
  total_records: number;
}

export interface RumHourly {
  hour: string;
  metric: string;
  avg: number;
  cnt: number;
}

export interface RumDetailed extends RumSummary {
  hourly: RumHourly[];
}

export interface SalesComparison {
  today: { count: number; amount: number };
  last_year: { count: number; amount: number };
  change_pct: number;
}

export interface GscData {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  date_range: string;
}

export interface GaRealtimeData {
  active5min: number;
  active30min: number;
  minutesData: number[];
  timestamp: number;
}

export interface ComplaintOrder {
  order_id: number;
  order_status: string;
  total: number;
  date_created: string;
  complaint_type: string;
  complaint_emoji: string;
  complaint_label: string;
  complaint_color: string;
  customer_name: string;
  customer_phone: string;
  order_comments: string;
  supplier_order_id: string;
}

export interface ComplaintsResponse {
  complaints: ComplaintOrder[];
  total: number;
  type_counts: Record<string, number>;
  timestamp: number;
}

export interface DailyChartDay {
  date: string;
  label: string;
  total: number;
  revenue: number;
  processing: number;
  completed: number;
  onhold: number;
  failed: number;
}

export interface PageSpeedPage {
  url?: string;
  name?: string;
  fcp_ms?: number;
  lcp_ms?: number;
  ttfb_ms?: number;
}

export interface PageSpeedData {
  avg_fcp_ms: number | null;
  pages: PageSpeedPage[];
  tested_at: string | null;
}

export interface AjaxSpeedAction {
  name?: string;
  action?: string;
  duration_ms?: number;
}

export interface AjaxSpeedData {
  avg_ajax_ms: number | null;
  actions: AjaxSpeedAction[];
  tested_at: string | null;
}

export interface CheckoutStep {
  name: string;
  duration_ms: number | null;
  status: string;
}

export interface CheckoutPerf {
  total_duration_ms: number | null;
  steps: CheckoutStep[];
  tested_at: string | null;
  success: boolean;
}

// ── Response types ────────────────────────────────────────────────────────

export interface DashboardData {
  pending_orders: PendingOrders;
  today: TodayStats;
  finance: FinanceData;
  health: HealthSummary;
  rum: RumSummary;
  sales_comparison: SalesComparison;
  delayed_packages: number;
  gsc: GscData | null;
  ga: GaRealtimeData | null;
  complaints_count: number;
  timestamp: number;
  generated_at: string;
}

export interface PerformanceData {
  rum: RumDetailed;
  health: {
    score: number;
    status: string;
    weights: Record<string, number>;
    checks: Record<string, unknown>;
    last_checked: string | null;
  };
  page_speed: PageSpeedData | null;
  ajax_speed: AjaxSpeedData | null;
  checkout: CheckoutPerf | null;
  timestamp: number;
}

export interface OrdersData {
  today: TodayStats;
  pending_orders: PendingOrders;
  daily_chart: DailyChartDay[];
  delayed_packages: number;
  sales_comparison: SalesComparison;
  timestamp: number;
}

export interface FinanceFullData {
  finance: FinanceData;
  payout_forecast: PayoutForecastDay[];
  today: TodayStats;
  returns_stock: { units: number; value: string };
  sales_comparison: SalesComparison;
  timestamp: number;
}

// ── WC Order types ────────────────────────────────────────────────────────

export interface WcOrderSummary {
  id: number;
  number: string;
  status: string;
  date_created: string;
  total: string;
  currency: string;
  payment_method: string;
  payment_method_title: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  customer_city: string;
  items_count: number;
  shipping_method: string;
  customer_note: string;
  supplier_order_id: string | null;
  tracking_status: string | null;
  tracking_number: string | null;
  has_complaint: boolean;
  complaint_text: string | null;
}

export interface WcOrdersResponse {
  orders: WcOrderSummary[];
  total: number;
  pages: number;
  page: number;
  per_page: number;
}

export interface WcOrderCounts {
  processing: number;
  'on-hold': number;
  completed: number;
  pending: number;
  cancelled: number;
  refunded: number;
  failed: number;
  all: number;
  [key: string]: number;
}

export interface WcOrderItem {
  id: number;
  name: string;
  sku: string;
  quantity: number;
  subtotal: string;
  total: string;
  price: string;
  image_url: string;
  product_url: string;
  supplier_cost: number | null;
}

export interface WcOrderAddress {
  first_name: string;
  last_name: string;
  company: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  email?: string;
  phone?: string;
}

export interface WcOrderNote {
  id: number;
  content: string;
  date_created: string;
  customer_note: boolean;
  added_by: string;
  is_system: boolean;
}

export interface WcOrderTotals {
  subtotal: string;
  shipping: string;
  discount: string;
  tax: string;
  total: string;
}

export interface WcOrderDetail extends WcOrderSummary {
  billing: WcOrderAddress;
  shipping: WcOrderAddress;
  items: WcOrderItem[];
  totals: WcOrderTotals;
  notes: WcOrderNote[];
  dates: {
    created: string;
    modified: string;
    paid: string | null;
    completed: string | null;
  };
  supplier_cost_total: number;
  shipping_methods: string[];
  fees: Array<{ name: string; total: string }>;
  coupons: Array<{ code: string; discount: string }>;
  transaction_id: string;
}

export interface WcOrdersParams {
  status?: string;
  page?: number;
  per_page?: number;
  search?: string;
}

// ── API methods ───────────────────────────────────────────────────────────

export const wpApi = {
  // Analytics endpoints
  getDashboard: () => apiFetch<DashboardData>('dashboard'),
  getPerformance: () => apiFetch<PerformanceData>('performance'),
  getOrders: (days = 7) => apiFetch<OrdersData>('orders', { days: String(days) }),
  getFinance: () => apiFetch<FinanceFullData>('finance'),
  getComplaints: (status?: string) => {
    const q: Record<string, string> = {};
    if (status) q.status = status;
    return apiFetch<ComplaintsResponse>('complaints', q);
  },

  // WC Order CRUD endpoints
  getWcOrders: (params?: WcOrdersParams) => {
    const q: Record<string, string> = {};
    if (params?.status) q.status = params.status;
    if (params?.page) q.page = String(params.page);
    if (params?.per_page) q.per_page = String(params.per_page);
    if (params?.search) q.search = params.search;
    return apiFetch<WcOrdersResponse>('wc-orders', q);
  },
  getWcOrderCounts: () => apiFetch<WcOrderCounts>('wc-orders/counts'),
  getWcOrderDetail: (id: number) => apiFetch<WcOrderDetail>(`wc-orders/${id}`),
  getWcOrderNotes: (id: number) => apiFetch<WcOrderNote[]>(`wc-orders/${id}/notes`),

  // Connection health check
  ping: async (): Promise<boolean> => {
    try {
      await apiFetch<DashboardData>('dashboard');
      return true;
    } catch {
      return false;
    }
  },
};
